/**
 * 未回答アンケートをまとめて回答するボタン（dev / design view 切替の隣）。
 *
 * ボタン自体は未回答が 2 件以上のときだけ出すが、**コンポーネントは常にマウントする**。
 * Slack DM の deep link の自動オープンをここが担っており、未回答が 1 件のときに
 * まるごと描画をやめると DM のリンクが動かなくなるため。開発 view で隠すのも
 * 同じ理由で `visible` によるボタンの出し分けにしており、マウント自体は止めない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 未回答アンケートの一括回答ボタンと Slack DM deep link の自動オープン
 * @graph-connects useMyEvaluationTasks [calls] アンケートタスク一覧取得
 * @graph-connects EvaluationSurveyModal [calls] 回答モーダル表示
 */
import { useEffect, useMemo, useState } from 'react';

import { EvaluationSurveyModal } from './EvaluationSurveyModal.js';
import { orderedFrom } from './survey-rows.js';
import { useMyEvaluationTasks } from '../../../hooks/useEvaluationSurvey.js';
import type { EvaluationTask } from '../../../types/idt-design-evaluation.js';

interface SurveyBatchAnswerButtonProps {
  /**
   * Slack DM の deep link (`/my-page?surveyChild=...`) で指定された子チケットキー。
   * 一致する未回答があれば、その件を先頭にしてモーダルを自動オープンする。
   */
  autoOpenChildIssueKey?: string;
  /**
   * ボタンを出すか。アンケートはデザイン view のテーブルにしか無いので、
   * 開発 view では `false` にして出さない（deep link のためマウントは続ける）。
   * 既定は `true`。
   */
  visible?: boolean;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 一括回答ボタン本体。未回答件数バッジ付きの桜色 pill ボタン（デザイン view のみ）とモーダル起動を担う
 * @graph-connects useMyEvaluationTasks [calls] アンケートタスク一覧取得
 * @graph-connects EvaluationSurveyModal [calls] 回答モーダル表示
 */
export function SurveyBatchAnswerButton({
  autoOpenChildIssueKey,
  visible = true,
}: SurveyBatchAnswerButtonProps) {
  const { data } = useMyEvaluationTasks();
  const [modalTasks, setModalTasks] = useState<EvaluationTask[] | null>(null);
  // deep link は 1 回だけ発火させる（閉じた直後に再オープンしてループしないように）。
  const [autoOpened, setAutoOpened] = useState(false);

  const pending = useMemo(
    () => (data?.tasks ?? []).filter((task) => task.state === 'pending'),
    [data],
  );

  useEffect(() => {
    if (autoOpened || autoOpenChildIssueKey === undefined) return;
    const index = pending.findIndex((task) => task.childIssueKey === autoOpenChildIssueKey);
    if (index < 0) return;
    setModalTasks(orderedFrom(pending, index));
    setAutoOpened(true);
  }, [autoOpenChildIssueKey, autoOpened, pending]);

  return (
    <>
      {visible && pending.length > 1 && (
        <button
          type="button"
          onClick={() => {
            setModalTasks(pending);
          }}
          /* 色はデザイン view の桜色（DevDesignViewSwitch のアクティブ側と同じ）に合わせる。 */
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-pink-300 px-3.5 py-1.5 text-xs font-medium whitespace-nowrap text-pink-900 transition-opacity hover:opacity-80"
        >
          まとめて回答する
          {/* 件数はタブ行のバッジと同じ見た目にして、同じ数を指していることを形で示す。 */}
          <span
            className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-[var(--color-warning)] px-1 text-[11px] font-bold text-white"
            aria-label={`未回答アンケート ${String(pending.length)} 件`}
          >
            {pending.length}
          </span>
        </button>
      )}
      {modalTasks && (
        <EvaluationSurveyModal
          tasks={modalTasks}
          open
          onClose={() => {
            setModalTasks(null);
          }}
        />
      )}
    </>
  );
}
